import { AI_MATE_URL } from '../../common/constants';
import type {
	ConvoAiErrorPayload,
	FetchIssueCreateDataProps,
	SuggestedIssue,
} from '../../common/types';

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export class ConvoAiError extends Error {
	constructor(
		readonly statusCode: number,
		readonly errorMessage: string,
	) {
		super(errorMessage);
	}
}

export const fetchAiSuggestedFields = async ({
	cloudId,
	confluencePage,
	highlightedText,
	signal,
	product = 'confluence',
	experienceId = 'ai-issue-create-confluence-single',
}: FetchIssueCreateDataProps): Promise<SuggestedIssue | undefined> => {
	try {
		const response = await fetch(AI_MATE_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
				'X-Experience-Id': experienceId,
				'X-Product': product,
				'Atl-CloudId': cloudId,
			},
			body: JSON.stringify({
				recipient_agent_named_id: 'ai_issue_create_agent',
				agent_input_context: {
					application: capitalizeFirstLetter(product),
					context: {
						confluence_page_markdown_plus: confluencePage,
						highlighted_text: highlightedText,
					},
					suggested_issues_config: {
						max_issues: 1,
						suggested_issue_field_types: [
							{
								issue_type: 'Task',
								fields: [
									{
										field_name: 'Summary',
										field_type: 'Short-Text',
									},
									{
										field_name: 'Description',
										field_type: 'Paragraph',
									},
								],
							},
						],
					},
				},
			}),
			credentials: 'include',
			signal,
		});

		if (!response.ok) {
			const errorPayload = await response.json();
			const error = handleErrorPayload(response.status, errorPayload);
			throw error;
		}

		const data = await response.json();
		const suggestion = parseData(data);
		if (!suggestion) {
			throw new Error(`Parsing response from ${experienceId} failed`);
		}
		return suggestion;
	} catch (error: Error | ConvoAiError | unknown) {
		if (error instanceof ConvoAiError) {
			throw error;
		}
		throw new Error(`Request to ${experienceId} agent failed`);
	}
};

export const parseData = (data: any): SuggestedIssue | undefined => {
	if (data.message_template && data.message_template === 'ACCEPTABLE_USE_VIOLATIONS') {
		throw new ConvoAiError(200, 'Ethical filtering error');
	}
	try {
		const content = data?.message?.content;
		if (!content) {
			return undefined;
		}
		const contentJson = JSON.parse(content);

		return {
			summary: contentJson.suggested_issues[0].field_values.Summary,
			description: contentJson.suggested_issues[0].field_values.Description,
		};
	} catch (error: Error | unknown) {
		throw new ConvoAiError(200, `Failed to parse JSON: ${error}`);
	}
};

export const handleErrorPayload = (
	statusCode: number,
	errorPayload: ConvoAiErrorPayload,
): ConvoAiError => {
	const { content, errorMessage, message_template } = errorPayload;
	// `content` is available when error is from assistance-service
	// `errorMessage` is available when error is from convo-ai (usually only AI enabled errors)
	// `message_template` is available when error is from assistance-service in case content is empty
	const message = content ? content : errorMessage ?? message_template;
	return new ConvoAiError(statusCode, message ?? 'Unknown error');
};
